/**
 * Check (plan §3 step 2, ui-spec §2.2): the fitted front elevation drawn over the reference
 * photo. The overlay is the FitResult rendered through the family generator (the working
 * design's BoardAssembly), mapped onto the piece's front face on the photo, not free lines.
 *
 * FND-16: when the fit result gives the front face as a quadrilateral (`evidence.frontQuad`,
 * corners TL, TR, BR, BL), the elevation is mapped through the homography from the unit square to
 * that quad, so the boards register on an angled photo. Without a quad the outline is the
 * frontal carcass rectangle ("Overlay drawn straight-on").
 */
import { computed } from 'vue';
import type { IntakeProposal } from '@/domain';
import { isFitResult } from '@/families/fit-schema';
import { shownBuild, ui } from './state';
import { lineLabel } from './format';
import { frontQuad } from './fit-extras';
import { mapUV, quadHomography } from '@/families/overlay';

export type Pt = { x: number; y: number };
export type Quad = [Pt, Pt, Pt, Pt];

/**
 * The projective map from the unit square ((0,0) top left, u to the right, v down) to a quad
 * (TL, TR, BR, BL): src/families/overlay.ts `quadHomography` and `mapUV` (Heckbert's square-to-quad).
 */
export function homography(q: Quad): (u: number, v: number) => Pt {
  const m = quadHomography(q);
  return (u, v) => mapUV(m, u, v);
}

export interface OverlayRect {
  id: string; role: string; label: string;
  /** Bounding box on the photo (normalised). */
  x: number; y: number; w: number; h: number;
  /** The board's outline on the photo: SVG polygon points, normalised. */
  points: string;
  /** Where the board's numbered tag points: its left end, half way up. */
  tagX: number; tagY: number;
  tag: string | null;
}
export interface OverlayModel {
  carcass: { left: number; right: number; top: number; bottom: number };
  /** The piece's front face on the photo (the carcass rectangle when the fit gave no quad). */
  quad: Quad;
  /** True when the fit gave the front face's perspective (frontQuad). */
  perspective: boolean;
  rects: OverlayRect[];
  /** Legacy (first-slice) proposals: measured shelf lines. */
  lines: { partId: string; y: number }[];
}

const r4 = (n: number) => Math.round(n * 10000) / 10000;
const rectQuad = (c: OverlayModel['carcass']): Quad => [{ x: c.left, y: c.top }, { x: c.right, y: c.top }, { x: c.right, y: c.bottom }, { x: c.left, y: c.bottom }];

/** The overlay for the current analysis, or null (manual setup, no photo, no fit). */
export const fitOverlay = computed<OverlayModel | null>(() => {
  const p = ui.analysis.proposal;
  if (!p || ui.analysis.status === 'manual' || ui.analysis.status === 'refused') return null;
  if (!isFitResult(p)) {
    const legacy = p as IntakeProposal;
    if (!legacy.evidence?.shelfLines?.length) return null;
    const carcass = legacy.evidence.carcass ?? { left: 0.05, right: 0.95, top: 0.05, bottom: 0.95 };
    return { carcass, quad: rectQuad(carcass), perspective: false, rects: [], lines: legacy.evidence.shelfLines.map((l) => ({ partId: l.partId, y: l.y })) };
  }
  if (!p.fitsAFamily) return null;
  const c = p.evidence.carcass;
  const fq = frontQuad.value;
  if (!fq && !(c.right > c.left && c.bottom > c.top)) return null;
  const quad: Quad = fq ?? rectQuad(c);
  const map = homography(quad);
  const b = shownBuild.value;
  const W = b.envelope.W, H = b.envelope.H;
  const at = (x: number, y: number) => map(x / W, 1 - y / H);
  const rects: OverlayRect[] = b.parts.filter((q) => q.role !== 'back').map((q) => {
    const pl = q.placement;
    const corners = [at(pl.x, pl.y + pl.sy), at(pl.x + pl.sx, pl.y + pl.sy), at(pl.x + pl.sx, pl.y), at(pl.x, pl.y)];
    const xs = corners.map((k) => k.x), ys = corners.map((k) => k.y);
    const x = Math.min(...xs), y = Math.min(...ys);
    const tag = at(pl.x, pl.y + pl.sy / 2);
    const tagged = q.role === 'top' || q.role === 'bottom' || (q.role === 'shelf' && (q.bay ?? 0) === 0);
    return {
      id: q.id, role: q.role, label: q.label, x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y,
      points: corners.map((k) => `${r4(k.x)},${r4(k.y)}`).join(' '),
      tagX: tag.x, tagY: tag.y, tag: tagged ? lineLabel(q.id) : null,
    };
  });
  const xs = quad.map((k) => k.x), ys = quad.map((k) => k.y);
  return { carcass: { left: Math.min(...xs), right: Math.max(...xs), top: Math.min(...ys), bottom: Math.max(...ys) }, quad, perspective: !!fq, rects, lines: [] };
});

/** Ids of the horizontal boards the overlay numbers (top, bay-1 shelves, bottom), top-down. */
export const overlayTagIds = computed(() => (fitOverlay.value?.rects.filter((r) => r.tag).sort((a, b) => a.tagY - b.tagY).map((r) => r.id) ?? fitOverlay.value?.lines.map((l) => l.partId) ?? []));

/**
 * The part of the photo Check shows (FND-17): the piece's outline with a margin, so the shelf
 * fills the card instead of sitting small in a wide product shot. Normalised x, y, w, h.
 */
export function cropFor(o: OverlayModel | null, imgAspect: number): { x: number; y: number; w: number; h: number } {
  if (!o) return { x: 0, y: 0, w: 1, h: 1 };
  const c = o.carcass;
  const mx = Math.max(0.06, (c.right - c.left) * 0.14), my = Math.max(0.05, (c.bottom - c.top) * 0.1);
  let x0 = Math.max(0, c.left - mx), x1 = Math.min(1, c.right + mx);
  let y0 = Math.max(0, c.top - my), y1 = Math.min(1, c.bottom + my);
  // keep the shown aspect between 1:2 and 2:1 (in pixels) so the card never becomes a sliver
  const px = (x1 - x0) * imgAspect, py = y1 - y0;
  if (px / py < 0.5) { const want = (py * 0.5) / imgAspect; const grow = (want - (x1 - x0)) / 2; x0 = Math.max(0, x0 - grow); x1 = Math.min(1, x1 + grow); }
  else if (px / py > 2) { const want = px / 2; const grow = (want - (y1 - y0)) / 2; y0 = Math.max(0, y0 - grow); y1 = Math.min(1, y1 + grow); }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
